import { useRouter } from 'found';
import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import styled from 'styled-components';
import { getCurrentAccount } from '../../account_helpers';
import { makeMutation } from '../../api/helpers';
import { DeleteService, OnboardMerchant } from '../../api/mutations';
import { Action, ActionList } from '../../components/ActionList';
import { ApiHeader } from '../../components/ApiHeader/ApiHeader';
import APIUsage from '../../components/APIUsage';
import Breadcrumb from '../../components/Breadcrumb';
import CopyText from '../../components/CopyText';
import { HelpText } from '../../components/HelpText';
import { HiddenContent } from '../../components/HiddenContent';
import { InfoLabel } from '../../components/InfoLabel';
import Button from '../../components/input/Button';
import FlatButton from '../../components/input/FlatButton';
import { PageSection } from '../../components/PageSection';
import Spacer from '../../components/Spacer';
import Table from '../../components/Table/Table';
import { useToastDispatch } from '../../components/Toast/ToastProvider';
import { ChargeType } from '../../components/typography/Typography';
import { routes } from '../../routes';
import { Api_account } from './__generated__/Api_account.graphql';
import { Api_service } from './__generated__/Api_service.graphql';

const Container = styled.div`
  // width: 1200px;
  flex: 1;
`;

const UsagePlaceholder = styled.div`
  min-height: 460px;
`;

const TotalsContainer = styled.div`
  display: flex;
`;

const ChargeCost = styled.div`
  display: flex;
`;

const ChargeCurrency = styled.div`
  color: #9094ac;
`;

const ChargeLeftExtraItems = styled.div`
  font-size: 14px;
`;

const AlertStatusText = styled.div`
  border-radius: 4px;
  background: #fff3dd;
  color: #c18f25;
  padding: 3px 13px;
  display: flex;
  align-items: center;
`;

const GoodStatusText = styled.div`
  border-radius: 4px;
  background: #ddffcf;
  color: #15a528;
  padding: 3px 13px;
  display: flex;
  align-items: center;
`;

type Props = {
  service: Api_service;
  account: Api_account;
};

function Api({ service, account }: Props) {
  document.title = `${service.name} API - PayPI`;
  const { router } = useRouter();

  const dispatch = useToastDispatch();

  const onDeleteService = async () => {
    if (!window.confirm('Are you sure you would like to delete this API?')) {
      return;
    }

    const variables = {
      serviceID: service.id,
    };

    try {
      await makeMutation(DeleteService, variables);
      dispatch({
        text: `'${service.name}' was deleted successfully`,
        alert: 'success',
      });
      router.push(routes.apis);
    } catch (err) {
      dispatch({ text: err.message, alert: 'error', stayFor: 4000 });
    }
  };

  const actions: Action[] = [];
  const setupStatus = account.merchant?.payments?.stripeSetupStatus;
  if (setupStatus !== 'ONBOARDED') {
    actions.push({
      title: 'Setup Stripe',
      description: 'To start taking payments you need to setup Stripe',
      actionText:
        setupStatus === 'MORE_REQUIRED' ? 'Complete setup' : 'Setup Stripe',
      onClick: async () => {
        try {
          const resp: any = await makeMutation(OnboardMerchant, {
            accountID: getCurrentAccount(),
          });
          window.open(resp.onboardMerchant.stripeOnboardURL, '_blank');
        } catch (err) {
          console.log('Unable to onboard', err);
        }
      },
    });
  }

  const hasCharges = service.charges?.edges?.length !== 0;
  if (!hasCharges) {
    actions.push({
      title: 'Add charge',
      description:
        'Charges allow you to take payments and track usage of your API',
      actionText: 'Add first Charge',
      onClick: () => router.push(routes.createCharge + '/' + service.id),
    });
  }

  const isFullySetup = hasCharges && setupStatus === 'ONBOARDED';
  return (
    <Container>
      <Breadcrumb
        crumbs={[
          {
            text: 'APIs',
            link: routes.apis,
          },
          {
            text: service.name,
          },
        ]}
      />
      <ApiHeader
        imageURL={service.imageURL}
        title={service.name}
        description={service.description}
        onUpdate={() => router.push(`${routes.updateApi}/${service.id}`)}
      />
      <Spacer vertical spacing={5} />
      {actions.length > 0 && (
        <PageSection title='Get setup for payments'>
          <ActionList
            title='Get started with APIs'
            description='Just a couple more things before you can start taking payments with
        your shiny new API Authentication, just setup Stripe (which allows you
        to retrieve money) and add some Charges (more info….)'
            actions={actions}
          />
          <Spacer vertical spacing={5} />
        </PageSection>
      )}
      <PageSection
        title='Development'
        subtitle='This information is used to authenticate users and make charges on your API.'
      >
        <Spacer vertical spacing={2} />
        <InfoLabel
          label='STATUS'
          tooltip={{
            title: 'Status',
            category: 'APIs',
            description: 'API setup status.',
            link: 'https://partner.paypi.dev/api/api#status',
          }}
        >
          {isFullySetup ? (
            <GoodStatusText>Live</GoodStatusText>
          ) : (
            <AlertStatusText>Setup required</AlertStatusText>
          )}
        </InfoLabel>
        <InfoLabel
          label='API SECRET'
          tooltip={{
            title: 'API Secret',
            category: 'APIs',
            description:
              'The secret key used to authenticate your API to PayPIs servers.',
            link: 'https://partner.paypi.dev/api/api#api-secret',
          }}
        >
          <HiddenContent
            secret={service?.secret}
            blurredText={service?.secret || 'Unable to fetch API secret'}
          />
        </InfoLabel>
        <InfoLabel
          label='SHARE URL'
          tooltip={{
            title: 'Share URL',
            category: 'APIs',
            description:
              'A URL you can redirect clients to so they can subscribe to your API',
            link: 'https://partner.paypi.dev/api/api#api-secret',
          }}
        >
          <CopyText>
            {hasCharges
              ? `${window.location.origin}/subscribe/${service.id}`
              : 'Create charges to view url'}
          </CopyText>
        </InfoLabel>
        <InfoLabel
          label='DEV SECRET'
          tooltip={{
            title: 'Dev Subscriber Secret',
            category: 'APIs',
            description:
              'Used to make test requests to your API for free. Requests are still shown in usage information but earnings are not added to your balance. ',
            link: 'https://partner.paypi.dev/api/api#dev-subscriber-secret',
          }}
        >
          <HiddenContent
            secret={
              service?.devSubscription?.secret ??
              'Unable to fetch subscription secret'
            }
            blurredText={
              service?.devSubscription?.secret ??
              'Unable to fetch subscription secret'
            }
          />
        </InfoLabel>
      </PageSection>
      <Spacer vertical spacing={5} />
      <PageSection title='Usage'>
        <Spacer vertical spacing={3} />
        <React.Suspense fallback={<UsagePlaceholder />}>
          <APIUsage
            rightItems={
              <TotalsContainer>
                {/* <TotalContainer>
                  <TotalTitle>Last 7 days</TotalTitle>
                  <TotalValue>£{service.day7?.totalCost}</TotalValue>
                </TotalContainer>
                <Spacer horizontal spacing={5} />
                <TotalContainer>
                  <TotalTitle>Last 30 days</TotalTitle>
                  <TotalValue>£{service.day30?.totalCost}</TotalValue>
                </TotalContainer>
                <Spacer horizontal spacing={5} />
                <TotalContainer>
                  <TotalTitle>This year</TotalTitle>
                  <TotalValue>£{service.year?.totalCost}</TotalValue>
                </TotalContainer> */}
              </TotalsContainer>
            }
          />
        </React.Suspense>
      </PageSection>
      <Spacer vertical spacing={5} />
      <PageSection
        title='Charges'
        subtitle='Your API should use charges to take payments from your users. Charges are also shown to your users as pricing information'
        leftExtraItems={
          <>
            <Spacer horizontal spacing={3} />
            <ChargeLeftExtraItems>
              <ChargeCurrency>
                {service.supportedCurrencies.join(', ')}
              </ChargeCurrency>
            </ChargeLeftExtraItems>
            {service.supportableCurrencies.length >=
              service.supportedCurrencies.length && (
              <>
                {' '}
                <Spacer horizontal spacing={2} />
                <FlatButton
                  darkenOnHover
                  color='#5C6EF8'
                  small
                  onClick={() => {
                    router.push(`${routes.addCurrency}/${service.id}`);
                  }}
                >
                  Add Currency
                </FlatButton>
              </>
            )}
          </>
        }
        extraItems={
          <FlatButton
            darkenOnHover
            color='#5C6EF8'
            onClick={() => router.push(routes.createCharge + '/' + service.id)}
          >
            Add charge
          </FlatButton>
        }
      >
        <Table
          rows={service?.charges?.edges?.map((service) => service?.node) ?? []}
          onRowClick={(row) => {
            router.push(`${routes.updateCharge}/${row.id}`);
          }}
          columns={[
            {
              headerName: 'Name',
              field: 'name',
              flex: 1,
            },
            {
              headerName: 'Identifier',
              component: (item) => <CopyText>{item.chargeIdentifier}</CopyText>,
              flex: 0.5,
            },
            {
              headerName: 'Type',
              flex: 0.5,
              component: (item) => <ChargeType>{item.chargeType}</ChargeType>,
            },
            {
              headerName: 'Cost',
              flex: 0.5,
              component: (val) => (
                <ChargeCost>
                  {val.prices.map((priceInfo: any) => (
                    <>
                      {priceInfo.amount > 0 ? `${priceInfo.amount}` : 'Free'}
                      <Spacer horizontal spacing={1} />
                      <ChargeCurrency>{priceInfo.currency}</ChargeCurrency>
                      <Spacer horizontal spacing={2} />
                    </>
                  ))}
                </ChargeCost>
              ),
            },
          ]}
        />
      </PageSection>
      <Spacer vertical spacing={5} />
      <PageSection title='API Management'>
        <HelpText>
          API deletion is permanant, you will not be able to retrieve your API
          once deleted
        </HelpText>
        <Spacer vertical spacing={2} />
        <Button
          background={'#F85C68'}
          color={'white'}
          width={'200px'}
          onClick={onDeleteService}
        >
          Delete API
        </Button>
      </PageSection>
      <Spacer vertical spacing={4} />
    </Container>
  );
}

export default createFragmentContainer(Api, {
  service: graphql`
    fragment Api_service on Service
      @argumentDefinitions(
        before7: { type: "Time!" }
        after7: { type: "Time!" }
        before30: { type: "Time!" }
        after30: { type: "Time!" }
        beforeYear: { type: "Time!" }
        afterYear: { type: "Time!" }
      ) {
      id
      name
      description
      imageURL
      secret
      supportedCurrencies
      supportableCurrencies
      charges {
        edges {
          node {
            id
            name
            description
            chargeIdentifier
            chargeType
            prices {
              amount
              currency
            }
          }
        }
      }
      devSubscription {
        secret
      }
      # day7: usage(before: $before7, after: $after7) {
      #   totalCosts
      # }
      # day30: usage(before: $before30, after: $after30) {
      #   totalCosts
      # }
      # year: usage(before: $beforeYear, after: $afterYear) {
      #   totalCosts
      # }
    }
  `,
  account: graphql`
    fragment Api_account on Account {
      merchant {
        payments {
          stripeSetupStatus
        }
      }
    }
  `,
});
